// Libs
import classNames from 'classnames/bind';
import { Box, Button, Stack, Typography, List } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import { BaseDatePicker, BaseInput, BaseLabelValueField, Loading } from '~/components/common';
import { DetailInvoice, ModalAttachFiles } from '~/components/module/invoice';
import { FileItem } from '~/components/module/document';
// Others
import { defaultValuesForm, schema } from './helper';
import { formatCurrency, formatDate } from '~/utils/helper/common';
import { IDocument } from '~/utils/interface/document';
import { EMPTY_STRING, MESSAGE_SEND_INVOICE_DEFAULT, POUND } from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { createInvoice, getInvoiceDetails, sendInvoice } from '~/thunks/invoice/invoiceThunk';
import {
  IInvoiceDetail,
  IQueryCreateInvoice,
  IQueryGetInvoiceDetails,
  IResInvoiceCreated,
  ISendInvoice,
  ISendInvoiceBody,
} from '~/utils/interface/invoice';
import { IInvoiceJobDetails, IParamsDetailJob } from '~/utils/interface/job';
import { getJobDetail } from '~/thunks/job/jobThunk';
import { DateFormatEnum, InvoiceSendModeEnum, TypeParamJobDetail } from '~/utils/enum/common';
// Styles, images, icons
import styles from './JobInvoice.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const JobInvoice = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job', 'admin/invoice']);
  const {
    control,
    handleSubmit,
    reset,
  } = useForm<ISendInvoice>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  const dispatch = useAppDispatch();
  const { id: jobId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isDisplayModalAttachFiles, setIsDisplayModalAttachFiles] = useState<boolean>(false);
  const [documentList, setDocumentList] = useState<IDocument[]>([]);
  const [isShowInvoice, setIsShowInvoice] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [jobDetail, setJobDetail] = useState<IInvoiceJobDetails>();
  const [invoiceCreated, setInvoiceCreated] = useState<IResInvoiceCreated>();
  const [invoiceDetails, setInvoiceDetails] = useState<IInvoiceDetail>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetJobDetail = useCallback(
    async (params: IParamsDetailJob): Promise<IInvoiceJobDetails | null> => {
      setIsLoading(true);

      return await dispatch(getJobDetail(params))
        .unwrap()
        .then((res) => {
          if (!res || !res?.data) return null;

          const data = res?.data as IInvoiceJobDetails;

          setJobDetail(data);
          return data;
        })
        .catch(() => {
          return null;
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch]
  );

  const handleGetInvoiceDetails = useCallback(
    async (params: IQueryGetInvoiceDetails): Promise<IInvoiceDetail | null> => {
      setIsLoading(true);

      return await dispatch(getInvoiceDetails(params))
        .unwrap()
        .then((res) => {
          if (!res || !res?.data) return null;

          const data: IInvoiceDetail = res?.data;

          setIsShowInvoice(true);
          setInvoiceDetails(data);

          const dataConvert: ISendInvoice = {
            message: data.message || EMPTY_STRING,
            ...(data.dueDate && { dueDate: formatDate(data.dueDate, DateFormatEnum.YYYY_MM_DD) }),
          };

          reset(dataConvert);

          return data;
        })
        .catch((error) => {
          return null;
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, reset]
  );

  //#region Implement Hook
  useEffect(() => {
    if (!jobId) return;

    const fetchData = async () => {
      const jobDetailRes = await handleGetJobDetail({
        jobId,
        type: TypeParamJobDetail.INVOICE,
      });

      if (!jobDetailRes?.invoiceId) return;

      await handleGetInvoiceDetails({
        invoiceId: jobDetailRes?.invoiceId,
        params: { jobId },
      });
    };

    fetchData();
  }, [jobId, invoiceCreated, handleGetJobDetail, handleGetInvoiceDetails]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSendForm = (data: ISendInvoice) => {
    if (!invoiceDetails?.id) return;

    const fileIds = documentList.map((item) => item.id);
    const newData = { ...data, fileIds, sendMode: InvoiceSendModeEnum.SEND_AND_UPDATE };
    const params: ISendInvoiceBody = {
      invoiceId: invoiceDetails?.id,
      body: newData,
    };

    setIsLoading(true);

    dispatch(sendInvoice(params))
      .unwrap()
      .then((res) => {})
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDisplayModalAttachFiles = () => {
    setIsDisplayModalAttachFiles(true);
  };

  const handleDeleteFile = (item: IDocument) => {
    setDocumentList((prevList) => prevList.filter((doc) => doc.id !== item.id));
  };

  const handleCloseModalAttachFiles = () => {
    setIsDisplayModalAttachFiles(false);
  };

  const handleSaveAttachFiles = (data: IDocument[]) => {
    setDocumentList(data);
    setIsDisplayModalAttachFiles(false);
  };

  const handleCreateInvoice = () => {
    if (!jobId) return;

    const params: IQueryCreateInvoice = {
      jobId: String(jobId),
      message: MESSAGE_SEND_INVOICE_DEFAULT,
    };

    setIsLoading(true);

    dispatch(createInvoice(params))
      .unwrap()
      .then((res) => {
        if (!res || !res?.data) return;

        setIsShowInvoice(true);
        setInvoiceCreated(res?.data);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} className={cx('container')} id='jobInvoicePage'>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {!isShowInvoice && !jobDetail?.invoiceId && (
            <Button
              variant='contained'
              className={cx('btnSend')}
              onClick={handleCreateInvoice}
              disabled={isLoading}
            >
              <img
                src={icons.invoiceIconCreateInvoice}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('btnSendIcon')}
              />

              <Typography component={'p'} className={cx('btnSendTxt')}>
                {t('invoice_btn_send_invoice', { ns: 'admin/invoice' })}
              </Typography>
            </Button>
          )}

          {isShowInvoice && jobDetail?.invoiceId && (
            <Stack
              component={'form'}
              spacing={'20px'}
              className={cx('form')}
              onSubmit={handleSubmit(handleSendForm)}
            >
              <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'flex-end'}
                component={'div'}
                spacing={'12px'}
              >
                <Button variant='contained' type='submit'>
                  {t('common_action_send', { ns: 'common/base' })}
                </Button>
              </Stack>

              <Box component={'div'} className={cx('formBody')}>
                <Box component={'div'} className={cx('viewVertical')}>
                  <Box component={'div'} className={cx('detailWrap')}>
                    <Box component={'div'} className={cx('title')}>
                      {t('invoice_detail_in_job_detail_title')}
                    </Box>

                    <BaseLabelValueField
                      label={t('invoice_detail_in_job_detail_invoice_id_label')}
                      value={invoiceDetails?.id ? `${POUND}${invoiceDetails?.id}` : EMPTY_STRING}
                    />

                    <BaseLabelValueField
                      label={t('invoice_detail_in_job_detail_invoice_date_label')}
                      value={formatDate(new Date())}
                    />

                    <BaseLabelValueField
                      label={t('invoice_detail_in_job_detail_due_date_label')}
                      value={
                        <Controller
                          control={control}
                          name='dueDate'
                          render={({ field: { value, onChange } }) => (
                            <BaseDatePicker
                              value={value}
                              onChange={(newDate) => {
                                onChange(newDate);
                                setInvoiceDetails((prev) => {
                                  if (!prev) return;
                                  return {
                                    ...prev,
                                    dueDate: newDate,
                                  };
                                });
                              }}
                            />
                          )}
                        />
                      }
                    />

                    <BaseLabelValueField
                      label={t('invoice_detail_in_job_detail_amount_due_label')}
                      value={invoiceDetails?.total ? formatCurrency(invoiceDetails.total) : EMPTY_STRING}
                    />
                  </Box>

                  <Box component={'div'} className={cx('messageWrap')}>
                    <Box component={'div'} className={cx('title')}>
                      {t('invoice_detail_in_job_detail_invoice_message_label')}
                    </Box>

                    <Controller
                      control={control}
                      name='message'
                      render={({ field: { value, onChange } }) => {
                        return (
                          <BaseInput
                            multiline
                            height={'auto'}
                            rows={6}
                            value={value}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              onChange(inputValue);
                              setInvoiceDetails((prev) => {
                                if (!prev) return;
                                return {
                                  ...prev,
                                  message: inputValue,
                                };
                              });
                            }}
                          />
                        );
                      }}
                    />
                  </Box>

                  <Box component={'div'} className={cx('attachmentWrap')}>
                    <Box component={'div'} className={cx('title')}>
                      {t('invoice_detail_in_job_detail_attachment_label')}
                    </Box>

                    <Button variant='contained' type='button' onClick={handleDisplayModalAttachFiles}>
                      {t('invoice_detail_in_job_detail_select_document_action')}
                    </Button>

                    <List className={cx('listDocument')}>
                      {documentList?.map((file) => (
                        <FileItem key={file.id} document={file} onDelete={handleDeleteFile} />
                      ))}
                    </List>
                  </Box>
                </Box>

                <Box component={'div'} className={cx('viewContent')}>
                  <DetailInvoice data={invoiceDetails} />
                </Box>

                {isDisplayModalAttachFiles && (
                  <ModalAttachFiles
                    isOpen={isDisplayModalAttachFiles}
                    data={documentList}
                    onClose={handleCloseModalAttachFiles}
                    onSave={handleSaveAttachFiles}
                  />
                )}
              </Box>
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default JobInvoice;
